import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Header } from '@/components/layout';
import { AnalysisMethod, useAnalysis } from '@/api/domains/analyses';
import { useViewedFindings } from './hooks/useViewedFindings';
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';
import { useAnalysisViewState } from './hooks/useAnalysisViewState';
import { useDocumentMetadata } from './hooks/useDocumentMetadata';
import { useCopyToClipboard } from './hooks/useCopyToClipboard';
import styles from './AnalysisDetail.module.css';
import {
  ComparisonView,
  FindingHeader,
  FindingsSidebar,
  NavigationControls,
  ValidationPanel,
  AIAnalysis,
  AnalysisBanner,
  AnalysisFailedView,
  AnalysisConfig,
  LoadingView,
  EmptyResultsView,
  ProcessingView
} from './components';
import { sortFindingsBySeverity } from './utils';
import { BackButton } from '@/components/ui';

const AnalysisDetail: React.FC = () => {
  const { analysisId } = useParams<{ analysisId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: analysis,
    isLoading,
    isError,
    error
  } = useAnalysis(analysisId!);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const viewState = useAnalysisViewState({ analysis, isLoading, isError });
  const documentMetadata = useDocumentMetadata(analysis);
  const { copiedTarget, handleCopy } = useCopyToClipboard();

  const findings = useMemo(() => {
    return analysis?.findings ? sortFindingsBySeverity(analysis.findings) : [];
  }, [analysis?.findings]);

  const { viewedFindings, markAsViewed } = useViewedFindings(analysisId!);

  useEffect(() => {
    if (analysis && searchParams.has('processing')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('processing');
      setSearchParams(newParams, { replace: true });
    }
  }, [analysis, searchParams, setSearchParams]);

  useEffect(() => {
    if (findings.length > 0 && findings[currentIndex]) {
      markAsViewed(findings[currentIndex].finding_id);
    }
  }, [currentIndex, findings, markAsViewed]);

  const handleNavigate = useCallback(
    (index: number) => {
      if (index >= 0 && index < findings.length) {
        setCurrentIndex(index);
        markAsViewed(findings[index].finding_id);
      }
    },
    [findings, markAsViewed]
  );

  const keyboardNavConfig = useMemo(
    () => ({
      enabled: findings.length > 0,
      currentIndex,
      maxIndex: findings.length - 1,
      onNavigate: handleNavigate
    }),
    [findings.length, currentIndex, handleNavigate]
  );

  useKeyboardNavigation(keyboardNavConfig);

  if (viewState === 'INITIAL_LOADING') {
    return <LoadingView />;
  }

  if (viewState === 'ERROR') {
    return (
      <>
        <Header />
        <AnalysisFailedView
          analysisName={analysis?.analysis_name}
          errorMessage={error?.message}
        />
      </>
    );
  }

  if (viewState === 'PROCESSING') {
    return <ProcessingView analysisName={analysis?.analysis_name} />;
  }

  if (viewState === 'EMPTY_RESULTS') {
    return <EmptyResultsView />;
  }

  const currentFinding = findings[currentIndex];

  if (!documentMetadata || !currentFinding) {
    return <LoadingView />;
  }

  return (
    <>
      <Header />
      <div className={styles.backButtonContainer}>
        <BackButton />
      </div>
      <div className={styles.container}>
        <FindingsSidebar
          findings={findings}
          currentIndex={currentIndex}
          viewedFindings={viewedFindings}
          isCollapsed={isSidebarCollapsed}
          onNavigate={handleNavigate}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />

        <div
          className={`${styles.mainContent} ${
            isSidebarCollapsed ? styles.mainContentExpanded : ''
          }`}
        >
          {analysis && (
            <AnalysisBanner
              analysisName={analysis.analysis_name}
              createdAt={analysis.created_at}
            />
          )}

          <NavigationControls
            currentIndex={currentIndex}
            totalFindings={findings.length}
            onNavigate={handleNavigate}
          />

          <FindingHeader
            finding={currentFinding}
            currentIndex={currentIndex}
            totalCount={findings.length}
            onCopy={handleCopy}
            copiedTarget={copiedTarget}
          />

          {currentFinding.rag_insight && (
            <AIAnalysis finding={currentFinding} />
          )}

          <ComparisonView
            finding={currentFinding}
            document1Name={documentMetadata.doc1Name}
            document2Name={documentMetadata.doc2Name}
            document1Source={documentMetadata.doc1Source}
            document2Source={documentMetadata.doc2Source}
          />

          <ValidationPanel finding={currentFinding} />

          {analysis?.metadata && (
            <AnalysisConfig
              method={analysis.method as AnalysisMethod}
              metadataJson={analysis.metadata}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default AnalysisDetail;
